import React, { useEffect, useRef, useState } from "react";
import { assetsService } from "../../services/assetsService";

interface CachedImageProps {
  src: string;
  alt: string;
  className: string;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
  assetType?: string;
  style?: React.CSSProperties;
}

const PRELOAD_TIMEOUT_MS = 10000;
const RETRY_DELAY_MS = 5000;

// crossOrigin only for hosts known to send CORS headers (our storage, Labbaik's
// storage); other external images may not, and would then fail to load
function crossOriginFor(url: string): "anonymous" | undefined {
  return url.includes("/digital-signage/") ||
    url.startsWith("https://icedxjqzbbtgksehvpwd.supabase.co/storage/")
    ? "anonymous"
    : undefined;
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const cross = crossOriginFor(url);
    if (cross) img.crossOrigin = cross;
    const timer = window.setTimeout(() => {
      img.src = "";
      reject(new Error("Image preload timed out"));
    }, PRELOAD_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error("Image preload failed"));
    };
    img.src = url;
  });
}

// Offline-first image: preloads before swapping, keeps the last good image on
// screen while the next loads, and never renders loading/error text.
const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  className,
  onLoad,
  onError,
  assetType = "poster",
  style,
}) => {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;
    const retryWhenOnline = () => load(1);

    const load = async (attempt: number) => {
      try {
        const url = await assetsService.getAsset(src, assetType);
        if (!url) throw new Error("No asset URL");
        await preloadImage(url);
        if (!cancelled) setDisplayUrl(url);
      } catch (err) {
        if (cancelled) return;
        if (attempt === 0) {
          retryTimer = window.setTimeout(() => load(1), RETRY_DELAY_MS);
        } else {
          console.error(`CachedImage: failed to load ${src}`, err);
          onErrorRef.current?.();
          // An image that failed during an outage shouldn't stay blank once it's over
          window.addEventListener("online", retryWhenOnline, { once: true });
        }
      }
    };

    load(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("online", retryWhenOnline);
    };
  }, [src, assetType]);

  if (!displayUrl) return null;

  return (
    <img
      src={displayUrl}
      alt={alt}
      className={className}
      style={style}
      crossOrigin={crossOriginFor(displayUrl)}
      onLoad={onLoad}
    />
  );
};

export default CachedImage;
